/**
 * Celebration.jsx — Festejo visual cuando se completa algo.
 *
 * Uso:
 *   import { useCelebration } from "./Celebration.jsx";
 *   const { celebrate } = useCelebration();
 *   celebrate({ tipo: "tarea-completada" });
 *
 * Tipos: "tarea-completada", "publicado", "kudo-recibido", "pulse-streak"
 *
 * Implementación sin dependencias: divs animados + CSS keyframes.
 */
import { useState, useCallback, useEffect, createContext, useContext } from "react";
import Glifo from "./Glifo.jsx";

const EMOJIS = ["🎉", "✨", "🌟", "💫", "⭐", "🎊", "🚀"];   // son claves de ícono (Glifo): el confetti se dibuja con íconos, no con emojis de texto

const CelebrationContext = createContext(null);

export function CelebrationProvider({ children }) {
  const [active, setActive] = useState(null);

  const celebrate = useCallback((opts = {}) => {
    const id = Date.now();
    const config = {
      tipo: opts.tipo || "tarea-completada",
      mensaje: opts.mensaje,
      emojis: opts.emojis || EMOJIS,
      duracion: opts.duracion || 2500,
      id,
    };
    setActive(config);
    setTimeout(() => {
      setActive(prev => (prev?.id === id ? null : prev));
    }, config.duracion);
  }, []);

  return (
    <CelebrationContext.Provider value={{ celebrate }}>
      {children}
      {active && <CelebrationOverlay config={active} />}
    </CelebrationContext.Provider>
  );
}

export function useCelebration() {
  const ctx = useContext(CelebrationContext);
  return ctx || { celebrate: () => {} };
}

function CelebrationOverlay({ config }) {
  const particles = [];
  for (let i = 0; i < 24; i++) {
    const left = Math.random() * 100;
    const delay = Math.random() * 0.3;
    const duration = 1.6 + Math.random() * 0.8;
    const emoji = config.emojis[i % config.emojis.length];
    particles.push(
      <span key={i}
            style={{
              left: `${left}%`,
              animationDelay: `${delay}s`,
              animationDuration: `${duration}s`,
              fontSize: `${1.4 + Math.random() * 1.2}rem`,
            }}
            className="confetti-particle absolute top-0">
        <Glifo g={emoji} className="text-[1em]" />
      </span>
    );
  }

  return (
    <>
      <div className="fixed inset-0 pointer-events-none z-[200] overflow-hidden">
        {particles}
      </div>
      {config.mensaje && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[201] pointer-events-none bg-card border-2 border-accent rounded-2xl px-8 py-4 shadow-2xl celebration-pop">
          <div className="text-center">
            <div className="text-4xl mb-1">{config.emojis[0]}</div>
            <div className="font-bold text-lg">{config.mensaje}</div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(-10vh) rotate(0deg);   opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 1; }
        }
        .confetti-particle {
          position: absolute;
          animation-name: confetti-fall;
          animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
          animation-iteration-count: 1;
        }
        @keyframes celebration-pop {
          0%   { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
          50%  { transform: translate(-50%, -50%) scale(1.1); opacity: 1; }
          80%  { transform: translate(-50%, -50%) scale(1.0); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1.0); opacity: 0; }
        }
        .celebration-pop {
          animation: celebration-pop 2.5s ease-out forwards;
        }
      `}</style>
    </>
  );
}
